import { useCallback, useMemo, useState } from 'react';

import { useConfirmPopup } from 'src/components/ConfirmPopup';
import { useAPStyleInsightsDebounced } from 'src/services/useAPStyleInsightsDebounced';
import { useRestrictActionPopup } from 'src/services/useRestrictActionPopup';
import { useCurrentCustomerId, useCustomerActions } from 'src/store/customer';
import { useCurrentWorkspace, useWorkspaceActions } from 'src/store/workspace';

export const useStyleGuideProps = ({
  localEditorRef,
  setSuccessMessage,
  setEditMode,
  readOnly,
}) => {
  const currentCustomerId = useCurrentCustomerId();
  const currentWorkspace = useCurrentWorkspace();
  const customerActions = useCustomerActions();
  const workspaceStoreActions = useWorkspaceActions();
  const openRestrictActionPopup = useRestrictActionPopup();
  const confirmPopup = useConfirmPopup();

  const apStyleGuidelinesEnabled = currentWorkspace?.apStyleGuidelinesEnabled;

  const [refreshKey, setRefreshKey] = useState(0);
  const increaseRefreshKey = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const { violations, fixedHtml, loading } = useAPStyleInsightsDebounced(
    {
      customerId: currentCustomerId,
      html: localEditorRef?.current?.getHtml(),
    },
    {
      disabled: !apStyleGuidelinesEnabled,
      refreshKey,
    }
  );

  const handleEnableApStyleInsights = useCallback(async () => {
    if (!customerActions.getIsCurrentUserAdmin()) {
      openRestrictActionPopup();
      return;
    }

    const confirmed = await confirmPopup({
      title: 'AP style compliance',
      content: `Do you want to turn ${
        apStyleGuidelinesEnabled ? 'off' : 'on'
      } AP style compliance validation?`,
      buttons: {
        confirm: 'Yes',
        cancel: 'Cancel',
      },
    });

    if (!confirmed) {
      return false;
    }

    await workspaceStoreActions.updateWorkspace({
      ...currentWorkspace,
      apStyleGuidelinesEnabled: !apStyleGuidelinesEnabled,
      defaultBrandVoiceId: currentWorkspace.defaultBrandVoice.id,
    });

    return true;
  }, [
    apStyleGuidelinesEnabled,
    confirmPopup,
    currentWorkspace,
    customerActions,
    openRestrictActionPopup,
    workspaceStoreActions,
  ]);

  const handleFixAll = useCallback(
    (event) => {
      event.stopPropagation();
      localEditorRef.current.replaceHtml(fixedHtml);
      setEditMode(true);
      setSuccessMessage('Updated to comply with AP style guidelines');
    },
    [localEditorRef, fixedHtml, setSuccessMessage, setEditMode]
  );

  return useMemo(
    () => ({
      loading,
      violations,
      apStyleGuidelinesEnabled,
      readOnly,
      onMounted: increaseRefreshKey,
      onSettingsClick: handleEnableApStyleInsights,
      onFixAllClick: handleFixAll,
    }),
    [
      loading,
      violations,
      apStyleGuidelinesEnabled,
      readOnly,
      increaseRefreshKey,
      handleEnableApStyleInsights,
      handleFixAll,
    ]
  );
};
